/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';

import { ApiOrgMarket } from '@kaiizen/kai-pro-api';

import { FlyOut } from '../../Widgets';
import {
  ADD_MARKETS_BODY_STYLE,
  ADD_MARKETS_CANCEL_BUTTON_STYLE,
  ADD_MARKETS_CHECKBOX_INPUT_STYLE,
  ADD_MARKETS_CHECKBOX_SELECTED_STYLE,
  ADD_MARKETS_CHECKBOX_STYLE,
  ADD_MARKETS_CHECKBOX_TICK_STYLE,
  ADD_MARKETS_COUNT_STYLE,
  ADD_MARKETS_DRAWER_STYLE,
  ADD_MARKETS_FOOTER_BUTTON_ROW_STYLE,
  ADD_MARKETS_FOOTER_STYLE,
  ADD_MARKETS_GROUP_LABEL_STYLE,
  ADD_MARKETS_GROUP_STYLE,
  ADD_MARKETS_LIST_STYLE,
  ADD_MARKETS_ROW_LABEL_STYLE,
  ADD_MARKETS_ROW_STYLE,
  ADD_MARKETS_SAVE_BUTTON_STYLE,
  ADD_MARKETS_SELECT_ALL_STYLE,
  ADD_MARKETS_TOP_ROW_STYLE,
} from './dashboardStyles';

export const AddMarketsFlyout = ({
  open,
  availableMarkets,
  initialSelectedMarketIds,
  isSaving,
  onClose,
  onSave,
}: {
  open: boolean;
  availableMarkets: ApiOrgMarket[];
  initialSelectedMarketIds: string[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (marketIds: string[]) => Promise<void>;
}) => {
  const [selectedMarketIds, setSelectedMarketIds] = useState<string[]>(initialSelectedMarketIds);

  useEffect(() => {
    if (!open) return;
    setSelectedMarketIds(initialSelectedMarketIds);
  }, [initialSelectedMarketIds, open]);

  const sortedMarkets = useMemo(
    () => [...availableMarkets].sort((a, b) => a.name.localeCompare(b.name)),
    [availableMarkets],
  );

  const groupedMarkets = useMemo(() => {
    const groups = new Map<string, ApiOrgMarket[]>();
    for (const market of sortedMarkets) {
      const firstLetter = market.name.trim().charAt(0).toUpperCase() || '#';
      const groupKey = /[A-Z]/.test(firstLetter) ? firstLetter : '#';
      groups.set(groupKey, [...(groups.get(groupKey) ?? []), market]);
    }
    return [...groups.entries()];
  }, [sortedMarkets]);

  const hasChanges = useMemo(() => {
    if (selectedMarketIds.length !== initialSelectedMarketIds.length) return true;
    const initialIds = new Set(initialSelectedMarketIds);
    return selectedMarketIds.some(id => !initialIds.has(id));
  }, [initialSelectedMarketIds, selectedMarketIds]);

  const toggleMarket = (marketId: string) => {
    setSelectedMarketIds(current => (
      current.includes(marketId)
        ? current.filter(id => id !== marketId)
        : [...current, marketId]
    ));
  };

  return (
    <FlyOut
      open={open}
      flyOutClosed={onClose}
      title='Add Markets'
      subheader='Select markets to show on your dashboard'
      drawerCss={ADD_MARKETS_DRAWER_STYLE}
      footerBorder={false}
      footer={
        <div css={ADD_MARKETS_FOOTER_STYLE}>
          <div css={ADD_MARKETS_FOOTER_BUTTON_ROW_STYLE}>
            <button type='button' css={ADD_MARKETS_CANCEL_BUTTON_STYLE} onClick={onClose} disabled={isSaving}>
              CANCEL
            </button>
            <button
              type='button'
              css={ADD_MARKETS_SAVE_BUTTON_STYLE}
              onClick={() => onSave(selectedMarketIds)}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>
      }
    >
      <div css={ADD_MARKETS_BODY_STYLE}>
        <div css={ADD_MARKETS_TOP_ROW_STYLE}>
          <div css={ADD_MARKETS_COUNT_STYLE}>
            {availableMarkets.length} MARKETS <span>({selectedMarketIds.length} Selected)</span>
          </div>
          <button
            type='button'
            css={ADD_MARKETS_SELECT_ALL_STYLE}
            onClick={() => setSelectedMarketIds(sortedMarkets.map(market => market.id))}
            disabled={isSaving || selectedMarketIds.length === sortedMarkets.length}
          >
            Select all
          </button>
        </div>

        <div css={ADD_MARKETS_LIST_STYLE}>
          {groupedMarkets.map(([letter, marketsInGroup]) => (
            <div key={letter} css={ADD_MARKETS_GROUP_STYLE}>
              <div css={ADD_MARKETS_GROUP_LABEL_STYLE}>{letter}</div>
              {marketsInGroup.map(market => {
                const isSelected = selectedMarketIds.includes(market.id);
                return (
                  <label key={market.id} css={ADD_MARKETS_ROW_STYLE}>
                    <input
                      css={ADD_MARKETS_CHECKBOX_INPUT_STYLE}
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => toggleMarket(market.id)}
                      disabled={isSaving}
                    />
                    <span css={isSelected ? ADD_MARKETS_CHECKBOX_SELECTED_STYLE : ADD_MARKETS_CHECKBOX_STYLE}>
                      {isSelected && <span css={ADD_MARKETS_CHECKBOX_TICK_STYLE}>✓</span>}
                    </span>
                    <span css={ADD_MARKETS_ROW_LABEL_STYLE}>{market.name}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </FlyOut>
  );
};
